import { useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiService, type Candidate } from '@/services/api'
import {
  ModalDeAccionDeCierre,
  type CandidatoParaCierre,
} from '@/features/rh/components/ModalDeAccionDeCierre'
import { ModalDeDescarteDeCandidato } from '@/features/rh/components/ModalDeDescarteDeCandidato'
import { ScheduleHiringManagerInterview } from '@/features/rh/components/HiringManagerInterviews/ScheduleHiringManagerInterview'
import { bandejaDeCierre } from '@/features/rh/api/bandejaDeCierre'
import {
  accionesDeCierre,
  mensajeDeFallo,
  type AccionDeCierre,
} from '@/features/rh/lib/bandejaDeCierre'
import { ACCION_DE_CIERRE_DE_TARJETA } from '@/features/rh/lib/reclutamientoTablero'

/**
 * Lo que pasa al elegir una acción sobre un candidato (SRH-339).
 *
 * Lo comparten el menú de la tarjeta del tablero y la ficha del candidato. Qué
 * acciones hay y con qué permisos lo sigue decidiendo `accionesDisponibles`;
 * esto solo ejecuta la que se eligió, así que las dos pantallas hacen lo mismo
 * con la misma acción: piden el motivo al descartar, confirman el cierre con el
 * mismo modal, agendan la entrevista del solicitante con el mismo modal.
 *
 * Lo único que cambia de una pantalla a otra va en las opciones:
 * - cómo avisar del resultado,
 * - qué recargar al terminar,
 * - cómo agendar la entrevista de RH, que en el tablero es un modal y en la
 *   ficha es su pestaña de entrevista.
 */
export interface OpcionesDeAccionesDelCandidato {
  /** Se llama después de cada cambio que se guardó, para refrescar la pantalla. */
  alActualizar: () => Promise<void> | void
  avisar: {
    exito: (titulo: string, mensaje: string) => void
    error: (titulo: string, mensaje: string) => void
  }
  /** La entrevista de RH la agenda cada pantalla a su manera. */
  agendarEntrevistaDeRH: (candidato: Candidate) => Promise<void> | void
}

/** El estatus al que lleva cada acción que solo cambia el estatus. */
const ESTATUS_DE_LA_ACCION: Record<string, { estatus: Candidate['status']; mensaje: string }> = {
  preselect: {
    estatus: 'en_proceso',
    mensaje: 'Candidato cambiado a "En Proceso". Ahora puede programar la entrevista.',
  },
  send_manager: {
    estatus: 'pre_seleccionado',
    mensaje: 'Candidato enviado al jefe solicitante',
  },
}

const QUE_PASO_EN_EL_CIERRE: Record<AccionDeCierre, string> = {
  seleccionar: 'quedó seleccionado',
  enviar_direccion: 'se mandó a validación de Dirección',
  descartar: 'quedó descartado',
}

export const useAccionesDelCandidato = ({
  alActualizar,
  avisar,
  agendarEntrevistaDeRH,
}: OpcionesDeAccionesDelCandidato): {
  ejecutar: (candidato: Candidate, accion: string) => Promise<void>
  /** El candidato sobre el que hay una acción en curso. */
  procesandoId: string | null
  /** Los modales de las acciones; se pintan una vez en la pantalla. */
  modales: ReactNode
} => {
  const navigate = useNavigate()
  const [procesandoId, setProcesandoId] = useState<string | null>(null)
  const [porDescartar, setPorDescartar] = useState<Candidate | null>(null)
  const [cierre, setCierre] = useState<{
    accion: AccionDeCierre
    candidato: CandidatoParaCierre
  } | null>(null)
  const [agendaDelSolicitanteDe, setAgendaDelSolicitanteDe] = useState<string | null>(null)

  /** Guarda el descarte. Es el único lugar donde el candidato pasa a descartado. */
  const confirmarDescarte = async (motivo: string, comentario: string) => {
    if (!porDescartar || !motivo) return
    setProcesandoId(porDescartar.id)
    try {
      await apiService.updateCandidateStatus(porDescartar.id, 'rechazado_evaluacion_rh', {
        motivo,
        comentario,
      })
      setPorDescartar(null)
      await alActualizar()
      avisar.exito('Candidato descartado', 'Quedó registrado el motivo del descarte.')
    } catch (err: unknown) {
      const fallo = err as { message?: string }
      avisar.error('Error', fallo?.message || 'No se pudo descartar al candidato')
    } finally {
      setProcesandoId(null)
    }
  }

  const alCerrarseElCandidato = async () => {
    if (!cierre) return
    const { accion, candidato } = cierre
    setCierre(null)
    await alActualizar()
    avisar.exito('¡Acción completada!', `${candidato.fullName} ${QUE_PASO_EN_EL_CIERRE[accion]}.`)
  }

  const ejecutar = async (candidato: Candidate, accion: string) => {
    // Cerrar el proceso usa la misma confirmación que la pantalla de Cierre.
    if (accion in ACCION_DE_CIERRE_DE_TARJETA) {
      setCierre({
        accion: ACCION_DE_CIERRE_DE_TARJETA[accion],
        candidato: { candidateId: candidato.id, fullName: candidato.fullName },
      })
      return
    }

    // Lo que RH puede hacer tras la decisión de Dirección depende de lo que
    // Dirección decidió, y ni el tablero ni la ficha lo traen: se pide a la bandeja.
    if (accion === 'cierre_confirmar') {
      setProcesandoId(candidato.id)
      try {
        const lista = await bandejaDeCierre.listar('decision_direccion')
        const encontrado = lista.find((c) => c.candidateId === candidato.id)
        const [accionDeCierre] = encontrado
          ? accionesDeCierre('decision_direccion', encontrado.directionDecision)
          : []
        if (encontrado && accionDeCierre) {
          setCierre({ accion: accionDeCierre, candidato: encontrado })
        } else {
          avisar.error('Error', 'Ya no está esperando la confirmación de RH. Se actualizó la pantalla.')
          await alActualizar()
        }
      } catch (error: unknown) {
        avisar.error('Error', mensajeDeFallo(error))
      } finally {
        setProcesandoId(null)
      }
      return
    }

    // Descartar pide el motivo antes: el cambio de estatus ocurre al guardar el
    // modal, en confirmarDescarte.
    if (accion === 'reject') {
      setPorDescartar(candidato)
      return
    }

    // SRH-308: la entrevista del solicitante usa su propio modal.
    if (accion === 'schedule_requester_interview') {
      setAgendaDelSolicitanteDe(candidato.id)
      return
    }

    if (accion === 'schedule_interview') {
      await agendarEntrevistaDeRH(candidato)
      return
    }

    if (accion === 'start_file') {
      navigate(`/hiring/hire/${candidato.id}`)
      return
    }

    const cambio = ESTATUS_DE_LA_ACCION[accion]
    if (!cambio) return

    setProcesandoId(candidato.id)
    try {
      await apiService.updateCandidateStatus(candidato.id, cambio.estatus)
      await alActualizar()
      avisar.exito('¡Acción completada!', cambio.mensaje)
    } catch (errCrudo: unknown) {
      const err = errCrudo as { message?: string }
      console.error('Error al actualizar estado del candidato:', err)
      avisar.error('Error', err.message || 'Error al actualizar el estado del candidato')
    } finally {
      setProcesandoId(null)
    }
  }

  const modales = (
    <>
      {cierre && (
        <ModalDeAccionDeCierre
          accion={cierre.accion}
          candidato={cierre.candidato}
          onCerrar={() => setCierre(null)}
          onHecho={() => void alCerrarseElCandidato()}
          onFallo={(mensaje, esConflicto) => {
            setCierre(null)
            avisar.error('No se pudo completar', mensaje)
            if (esConflicto) void alActualizar()
          }}
        />
      )}

      {porDescartar && (
        <ModalDeDescarteDeCandidato
          candidato={porDescartar}
          guardando={!!procesandoId}
          onCancelar={() => setPorDescartar(null)}
          onGuardar={confirmarDescarte}
        />
      )}

      <ScheduleHiringManagerInterview
        isOpen={agendaDelSolicitanteDe !== null}
        onClose={() => setAgendaDelSolicitanteDe(null)}
        onSuccess={async () => {
          setAgendaDelSolicitanteDe(null)
          await alActualizar()
          avisar.exito('Entrevista agendada', 'La entrevista con el solicitante quedó programada.')
        }}
        preSelectedCandidateId={agendaDelSolicitanteDe ?? undefined}
      />
    </>
  )

  return { ejecutar, procesandoId, modales }
}
